import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import ActionButton from "../../components/common/buttons/ActionButton";
import SelectField from "../../components/common/fields/SelectField";
import { useSalon } from "../../hooks/useSalon";
import { useCountries } from "../../hooks/useCountries";
import { filterSalonsWithCoords, getRegionView } from "./SalonMap.utils";

// Bundlers do not resolve Leaflet's default icon paths — point them at local assets.
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const MAP_HEIGHT = "calc(100vh - 320px)";
const MAP_BORDER_RADIUS = "8px";
const REGION_SELECT_WIDTH = 240;

type MapCountry = { id: number; name: string; code: string };

const SalonMapView = ({ countries }: { countries: MapCountry[] }) => {
  const navigate = useNavigate();
  const [regionCode, setRegionCode] = useState<string>(
    countries[0]?.code ?? ""
  );
  const view = getRegionView(regionCode);
  const [{ data, isLoading, error }, { mutate }] = useSalon({
    page: 0,
    size: 1000,
    country: regionCode,
  });
  const salons = filterSalonsWithCoords(data ?? []);

  const regionOptions = countries.map((country) => ({
    label: country.name,
    value: country.code,
  }));

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="subtitle2">Region</Typography>
        <Box sx={{ width: REGION_SELECT_WIDTH }}>
          <SelectField
            options={regionOptions}
            value={regionCode}
            placeholder="Region"
            onChange={(value) => value !== null && setRegionCode(String(value))}
          />
        </Box>
      </Box>

      {error ? (
        <Alert
          severity="error"
          action={
            <ActionButton onClick={() => mutate()}>Retry</ActionButton>
          }
        >
          Failed to load salons.
        </Alert>
      ) : isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : salons.length === 0 ? (
        <Alert severity="info">No salons with location in this region.</Alert>
      ) : (
        <MapContainer
          key={regionCode}
          center={view.center}
          zoom={view.zoom}
          style={{ height: MAP_HEIGHT, width: "100%", borderRadius: MAP_BORDER_RADIUS }}
        >
          <TileLayer
            attribution='Sources: Esri, HERE, Garmin, USGS, Intermap, INCREMENT P, NRCan, Esri Japan, METI, Esri China (Hong Kong), Esri Korea, Esri (Thailand), NGCC, (c) OpenStreetMap contributors, and the GIS User Community'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
          />
          {salons.map((salon) => (
            <Marker key={salon.id} position={[salon.lat, salon.lon]}>
              <Popup>
                <Typography variant="subtitle2">{salon.salon_name}</Typography>
                <Typography variant="caption">{salon.status}</Typography>
                <Box sx={{ mt: 1 }}>
                  <ActionButton onClick={() => navigate(`/salon/${salon.id}`)}>
                    View
                  </ActionButton>
                </Box>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      )}
    </Box>
  );
};

const SalonMap = () => {
  const [{ data: countries, isLoading, error }, { mutate }] = useCountries();
  const activeCountries = countries.filter((country) => country.active);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || activeCountries.length === 0) {
    return (
      <Alert
        severity="error"
        action={
          <ActionButton onClick={() => mutate()}>Retry</ActionButton>
        }
      >
        Failed to load regions.
      </Alert>
    );
  }
  return <SalonMapView countries={activeCountries} />;
};

export default SalonMap;
